import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'

import SkeletonTable from './SkeletonTable'

export type SkeletonDataGridToolbar = 'none' | 'icons' | 'search' | 'full'
export type SkeletonDataGridFooter = 'none' | 'summary' | 'pagination'

export type SkeletonDataGridProps = {
    rows?: number
    columns?: number
    subTabs?: number
    toolbar?: SkeletonDataGridToolbar
    footer?: SkeletonDataGridFooter
    action?: boolean
    fill?: boolean | number | string
}

const fillHeight = (fill: boolean | number | string) => {
    if (fill === false) return undefined
    if (fill === true) return 'calc(100vh - 260px)'
    return fill
}

export default function SkeletonDataGrid(props: Readonly<SkeletonDataGridProps>) {
    const {
        rows = 14,
        columns = 7,
        subTabs = 0,
        toolbar = 'full',
        footer = 'summary',
        action = false,
        fill = true,
    } = props

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            {subTabs > 0 && (
                <Stack
                    direction="row"
                    spacing={3}
                    sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    {Array.from({ length: subTabs }, (_, index) => (
                        <Skeleton key={index} variant="text" width={90 + ((index * 31) % 80)} />
                    ))}
                </Stack>
            )}

            <Paper
                variant="outlined"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: fillHeight(fill),
                    overflow: 'hidden',
                }}
            >
                {toolbar !== 'none' && (
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                            px: 2,
                            py: 1.5,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            rowGap: 1.5,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{ alignItems: 'center', flexGrow: 1, minWidth: 240 }}
                        >
                            {toolbar === 'full' && (
                                <Skeleton variant="rounded" width={150} height={24} />
                            )}
                            {(toolbar === 'full' || toolbar === 'search') && (
                                <Skeleton
                                    variant="rounded"
                                    height={32}
                                    sx={{ flexGrow: 1, minWidth: 180 }}
                                />
                            )}
                            {toolbar === 'full' && (
                                <>
                                    <Skeleton variant="rounded" width={90} height={24} />
                                    <Skeleton variant="rounded" width={220} height={40} />
                                </>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={1.5}>
                            {Array.from({ length: 4 }, (_, index) => (
                                <Skeleton key={index} variant="circular" width={24} height={24} />
                            ))}
                        </Stack>
                    </Stack>
                )}

                <Box sx={{ flexGrow: 1 }}>
                    <SkeletonTable rows={rows} columns={columns} showToolbar={false} />
                </Box>

                {footer !== 'none' && (
                    <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{ px: 2, py: 1.5, alignItems: 'center', justifyContent: 'flex-end' }}
                    >
                        {footer === 'summary' ? (
                            <Skeleton variant="text" width={280} />
                        ) : (
                            <>
                                <Skeleton variant="text" width={150} />
                                <Skeleton variant="text" width={90} />
                                <Skeleton variant="circular" width={22} height={22} />
                                <Skeleton variant="circular" width={22} height={22} />
                            </>
                        )}
                    </Stack>
                )}
            </Paper>

            {action && (
                <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                    <Skeleton variant="rounded" width={150} height={40} />
                </Stack>
            )}
        </Stack>
    )
}
